import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
  useImperativeHandle,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Form from '@rjsf/core';
import { customizeValidator } from '@rjsf/validator-ajv8';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import UserContext from '@/contexts/UserContext';
import { customFormats } from '@/helpers/isValidDateTimeFormat';
import Loading from '@/Loading';
import ArrayFieldTemplate from '@/orderForm/fields/ArrayFieldTemplate';
import AttachmentField from '@/orderForm/fields/AttachmentField';
import AttachmentFiles from '@/orderForm/fields/AttachmentFiles';
import AutoCompleteOrCreate from '@/orderForm/fields/AutoCompleteOrCreate';
import BrySignersField from '@/orderForm/fields/BrySignersField';
import DocuSignFile from '@/orderForm/fields/DocuSignFile';
import DocuSignSignersField from '@/orderForm/fields/DocuSignSigners';
import EditRequestFormFieldDisable from '@/orderForm/fields/EditRequestFormFieldDisable';
import FileUpload from '@/orderForm/fields/FileUpload';
import KeysLocationField from '@/orderForm/fields/KeysLocationField';
import RealEstateAdressesSelectField from '@/orderForm/fields/RealEstateAdressesSelectField';
import RecipientsField from '@/orderForm/fields/RecipientsField/RecipientsField';
import CustomFieldDisable from '@/orderForm/fields/RequestFormFieldDisable';
import People from '@/orderForm/fields/RequestFormPeople';
import Person from '@/orderForm/fields/RequestFormPerson';
import RequestFormPreviousFile from '@/orderForm/fields/RequestFormPreviousFile';
import RequestFormPreviousPanel from '@/orderForm/fields/RequestFormPreviousPanel';
import RequestInfoField from '@/orderForm/fields/RequestInfoField';
import SuitorsArrayFieldTemplate from '@/orderForm/fields/SuitorsArrayFieldTemplate';
import TagsFieldTemplate from '@/orderForm/fields/TagsFieldTemplate';
import AddressField from '@/orderForm/fields/AddressField';
import AccessInformationField from '@/orderForm/fields/AccessInformationField';
import PropertyAccessField from '@/orderForm/fields/PropertyAccessField';
import makeWidgetsFn from '@/orderForm/OrderForm/makeWidgets';
import * as OrderFormTitleIcons from '@/orderForm/OrderForm/OrderFormTitleIcons';
import containerClasses from '@/orderForm/style.module.scss';
import deepMerge from '@/orderForm/utils/deepMerge';

const validator = customizeValidator({ customFormats });

const transformErrorsMessages = {
  required: 'requiredField',
  minItems: 'DocuSign.errors.minItems',
  minLength: 'minLength',
  format: 'invalidFormat',
  type: 'invalidFormat',
};

const hasFormData = (data) => !!data && typeof data === 'object' && Object.keys(data).length > 0;

const makeZipcodeOptional = schema => {
  if (Array.isArray(schema)) return schema.map(makeZipcodeOptional);
  if (!schema || typeof schema !== 'object') return schema;

  const schemaCopy = Object.fromEntries(Object.entries(schema).map(([key, value]) => [key, makeZipcodeOptional(value)]));

  if (schemaCopy.properties?.zipcode && Array.isArray(schemaCopy.required)) {
    schemaCopy.required = schemaCopy.required.filter(field => field !== 'zipcode');
  }

  return schemaCopy;
};



export const hiddenTitleKeys = [
  'schedule', 'acAddress', 'paAddress', 'keysDevolutionAddress',
  'income', 'main', 'desiredPropertyAddress', 'documents', 'recipients', 'signers', 'address', 'tags'
];

const forbiddenObjectTitles = [
  'Administradora', 'Captador', 'Construtora', 'Vistoriador',
  'main', 'income'
];

const titleIcons = {
  parties:              <OrderFormTitleIcons.InvolvedParties      viewBox="0 0 34.016 34.016" />,
  keys:                 <OrderFormTitleIcons.KeysIcon             viewBox="0 0 34.016 34.016" />,
  mainAddress:          <OrderFormTitleIcons.Property             viewBox="0 0 34.016 34.016" />,
  address:              <OrderFormTitleIcons.Address              viewBox="0 0 34.016 34.016" />,
  location_references:  <OrderFormTitleIcons.Address              viewBox="0 0 34.016 34.016" />,
  details:              <OrderFormTitleIcons.Details              viewBox="0 0 34.016 34.016" />,
  construction_details: <OrderFormTitleIcons.Details              viewBox="0 0 34.016 34.016" />,
  solicitations:        <OrderFormTitleIcons.Details              viewBox="0 0 34.016 34.016" />,
  photoBuildingDetails: <OrderFormTitleIcons.Details              viewBox="0 0 34.016 34.016" />,
  client:               <OrderFormTitleIcons.Client               viewBox="0 0 34.016 34.016" />,
  clients:              <OrderFormTitleIcons.Client               viewBox="0 0 34.016 34.016" />,
  franchisees:          <OrderFormTitleIcons.Franchisee           viewBox="0 0 34.016 34.016" />,
  previous:             <OrderFormTitleIcons.Previous             viewBox="0 0 34.016 34.016" />,
  additionals:          <OrderFormTitleIcons.Previous             viewBox="0 0 34.016 34.016" />,
  cost:                 <OrderFormTitleIcons.Previous             viewBox="0 0 34.016 34.016" />,
  recommendation:       <OrderFormTitleIcons.Recommendation />,
  tags:                 <OrderFormTitleIcons.Tag />,
  accessInformation:    <OrderFormTitleIcons.AccessInformation />,
  accompaniedInspection:<OrderFormTitleIcons.AccompaniedInspection />,
  propertyInformations: <OrderFormTitleIcons.PropertyInformations />,
  propertyAccess:       <OrderFormTitleIcons.PropertyAccess />,
  interestedPerson:     <OrderFormTitleIcons.InterestedPerson />,
  potentialSchedule:    <OrderFormTitleIcons.PotentialSchedule />,
  additionalCosts:      <OrderFormTitleIcons.AdditionalCosts />,
  reportSubmission:     <OrderFormTitleIcons.ReportSubmission />,
  vacationNotice:       <OrderFormTitleIcons.ReportSubmission />,
  desired_property:     <OrderFormTitleIcons.Property />,
  main_suitor:          <OrderFormTitleIcons.Client />,
  main_suitor_address:  <OrderFormTitleIcons.Address />,
  suitors:              <OrderFormTitleIcons.ClientPlus />,
  budgetDuringInspection:<OrderFormTitleIcons.BudgetDuringInspection />,
  tourAccessInfo:       <OrderFormTitleIcons.TourVirtualAcessInfo />,
};



const isFieldHidden = (prop) => {
  if (prop.hidden) return true;

  const uiSchema = prop.content?.props?.uiSchema;
  if (uiSchema?.['ui:widget'] === 'hidden') return true;
  if (uiSchema?.['ui:field'] === 'hidden') return true;

  const schema = prop.content?.props?.schema;
  if (schema?.type === 'object') {
    const keys = Object.keys(schema.properties || {});
    if (keys.length === 0) return true;

    const allChildrenHidden = keys.every(key => {
      const childUi = uiSchema?.[key];
      return childUi?.['ui:widget'] === 'hidden' || childUi?.['ui:field'] === 'hidden';
    });
    if (allChildrenHidden) return true;
  }

  return false;
};

const ObjectFieldTemplate = ({ properties, title, required, idSchema, formContext }) => {
  const isRoot = idSchema?.$id === 'root';
  const id = idSchema?.$id || '';
  

  const isNestedAddress = id.includes('_address') && id.split('_').length > 2 && !id.startsWith('root_main_suitor_address');
  const isForbidden = forbiddenObjectTitles.some(t => t.toLowerCase() === (title || '').toLowerCase()) || hiddenTitleKeys.includes(title);
  
  const { orderType, me } = formContext || {};
  const isMarketplaceFichaCerta = orderType === 'marketplace_fichacerta';
  const isClient = me?.authRole === 'ROLE_CLIENT';

  const isArrayItem = /-\d+$/.test(title || '');
  const hasTitle = !!title && !isRoot && !isNestedAddress && !isForbidden && !isArrayItem;

  return (
    <div className="flex flex-col">
      {hasTitle && (
        <TitleField title={title} required={required} />
      )}
      <div
        className={`grid grid-cols-12 gap-x-[30px] gap-y-2`}
        style={{ paddingLeft: '1vw', paddingRight: '1vw' }}
      >
        {properties.map((prop) => {
          if (isFieldHidden(prop)) return null;

          const isIndented = prop.content?.props?.uiSchema?.['ui:options']?.indented;
          const cols = isIndented ? 'col-span-12'
            : (isMarketplaceFichaCerta || isClient ? gridColsClient : gridCols)[prop.name] || 'col-span-12';

          const isObject = prop.content?.props?.schema?.type === 'object';
          const isTransparent = ['income', 'main', 'address'].includes(prop.name);
          const spacingClass = isObject && !isTransparent ? 'mb-2' : 'mb-0';
          
          return (
            <div className={`${cols} ${spacingClass}`} key={prop.content?.key}>
              {prop.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FieldTemplate = ({ classNames, help, description, children, label, errors, idSchema, rawErrors, uiSchema }) => {
  const id = idSchema?.$id || '';
  const skipIds = ['root_acAddress', 'root_paAddress', 'root_desired_property_address', 'root_main_suitor_address'];
  const shouldSkip = skipIds.includes(id) || (label && ['income', 'main'].includes(label.toLowerCase()));

  if (shouldSkip) {
    return <div className={containerClasses.removeNested}>{children}</div>;
  }

  const widget = uiSchema?.['ui:widget'];
  const field = uiSchema?.['ui:field'];
  const hasWidgetErrors =
    (rawErrors && rawErrors.length > 0 && children?.props && 'rawErrors' in children.props) ||
    (typeof widget === 'string' &&
      [
        'customNumberWidget',
        'customStringWidget',
        'zipcodeWidget',
        'customPriceWidget',
        'customPriceAreaWidget',
        'customPriceStringWidget',
        'customMultilineWidget',
        'customAreaWidget',
        'customAreaDisableWidget',
      ].includes(widget)) ||
    (typeof field === 'string' &&
      [
        'autoCompleteOrCreate',
        'attachmentFiles',
        'accessInformationField',
        'propertyAccessField',
        'tagsField',
      ].includes(field));

  return (
    <div className={classNames}>
      {children}
      {description}
      {!hasWidgetErrors && errors && (
        <div className="mt-1 text-[12px] font-normal text-red-500">{errors}</div>
      )}
      {help}
    </div>
  );
};

const gridCols = {
  simple: 'col-span-12 md:col-span-4', foursided: 'col-span-12 md:col-span-4', others: 'col-span-12 md:col-span-4',
  complement: 'col-span-12 md:col-span-4', number: 'col-span-12 md:col-span-4', state: 'col-span-12 md:col-span-4',
  condominiumCost: 'col-span-12 md:col-span-4', iptuCost: 'col-span-12 md:col-span-4', rentCost: 'col-span-12 md:col-span-4',
  companionName: 'col-span-12 md:col-span-4', companionContact: 'col-span-12 md:col-span-4', scheduleDate: 'col-span-12 md:col-span-4',
  area: 'col-span-12 md:col-span-6', furnished: 'col-span-12 md:col-span-6', building_type: 'col-span-12 md:col-span-6',
  capture_schedule_date: 'col-span-12 md:col-span-6', purpose: 'col-span-12 md:col-span-6', link: 'col-span-12 md:col-span-6',
  highlights: 'col-span-12 md:col-span-6', fullName: 'col-span-12 md:col-span-6', whatsapp: 'col-span-12 md:col-span-6',
  date1: 'col-span-12 md:col-span-6', time1: 'col-span-12 md:col-span-6', date2: 'col-span-12 md:col-span-6', time2: 'col-span-12 md:col-span-6',
  roomsCount: 'col-span-12 md:col-span-6', suitesCount: 'col-span-12 md:col-span-6', contact: 'col-span-12 md:col-span-6',
  name: 'col-span-12 md:col-span-6', street: 'col-span-12 md:col-span-8', neighborhood: 'col-span-12 md:col-span-8', city: 'col-span-12 md:col-span-8',
  property_code: 'col-span-12', real_estate_type: 'col-span-12',
  rent_value: 'col-span-12 md:col-span-4', iptu_value: 'col-span-12 md:col-span-4', condominium_value: 'col-span-12 md:col-span-4',
  address: 'col-span-12', main_suitor_address: 'col-span-12', acAddress: 'col-span-12', paAddress: 'col-span-12',
  income_origin: 'col-span-12 md:col-span-6', income_value: 'col-span-12 md:col-span-6',
  suitor_name: 'col-span-12 md:col-span-6', suitor_natural_document: 'col-span-12 md:col-span-6',
  suitor_legal_document: 'col-span-12 md:col-span-6', reside_or_participant: 'col-span-12',
  income: 'col-span-12', main: 'col-span-12',
};

const gridColsClient = {
  simple: 'col-span-12 md:col-span-4', foursided: 'col-span-12 md:col-span-4', others: 'col-span-12 md:col-span-4',
  companionName: 'col-span-12 md:col-span-4', companionContact: 'col-span-12 md:col-span-4', scheduleDate: 'col-span-12 md:col-span-4',
  condominiumCost: 'col-span-12 md:col-span-4', iptuCost: 'col-span-12 md:col-span-4', rentCost: 'col-span-12 md:col-span-4',
  street: 'col-span-12 md:col-span-6', neighborhood: 'col-span-12 md:col-span-6', area: 'col-span-12 md:col-span-6',
  building_id: 'col-span-12 md:col-span-6', furnished: 'col-span-12 md:col-span-6', link: 'col-span-12 md:col-span-6',
  highlights: 'col-span-12 md:col-span-6', fullName: 'col-span-12 md:col-span-6', whatsapp: 'col-span-12 md:col-span-6',
  date1: 'col-span-12 md:col-span-6', time1: 'col-span-12 md:col-span-6', date2: 'col-span-12 md:col-span-6', time2: 'col-span-12 md:col-span-6',
  roomsCount: 'col-span-12 md:col-span-6', suitesCount: 'col-span-12 md:col-span-6', contact: 'col-span-12 md:col-span-6',
  name: 'col-span-12 md:col-span-6', complement: 'col-span-12 md:col-span-3', number: 'col-span-12 md:col-span-3',
  state: 'col-span-12 md:col-span-3', city: 'col-span-12 md:col-span-3', building_type: 'col-span-12 md:col-span-3',
  capture_schedule_date: 'col-span-12 md:col-span-6', purpose: 'col-span-12 md:col-span-3',
  property_code: 'col-span-12', real_estate_type: 'col-span-12',
  rent_value: 'col-span-12 md:col-span-4', iptu_value: 'col-span-12 md:col-span-4', condominium_value: 'col-span-12 md:col-span-4',
  income_origin: 'col-span-12 md:col-span-7', income_value: 'col-span-12 md:col-span-5',
  suitor_type: 'col-span-12 md:col-span-4', suitor_name: 'col-span-12 md:col-span-5',
  suitor_natural_document: 'col-span-12 md:col-span-3', suitor_legal_document: 'col-span-12 md:col-span-3',
  reside_or_participant: 'col-span-12', income: 'col-span-12', main: 'col-span-12',
  address: 'col-span-12', main_suitor_address: 'col-span-12', acAddress: 'col-span-12', paAddress: 'col-span-12',
};

const fieldStyles = {
  title:   { fontFamily: 'Open Sans', fontWeight: 700, fontSize: '16px', color: '#212529', display: 'inline-block' },
  subtitle:{ paddingLeft: '2vw', paddingRight: '2vw', paddingBottom: '1vh', fontWeight: 'normal', fontSize: '14px' },
  content: { paddingLeft: '1vw', paddingRight: '1vw' },
  error:   { fontFamily: 'Open Sans', fontWeight: 'normal', fontSize: '12px' },
};


export const TitleField = (props) => {
  const { title, required } = props;
  const intl = useIntl();
  if (hiddenTitleKeys.includes(title)) return null;

  const icon = titleIcons[title];
  let titleKey = title;
  if (title === 'details') titleKey = `${title}_title`;

  const heading = (
    <div className="mb-[10px] mt-[-4px] flex flex-wrap items-center gap-2">
      {icon && (
        <div className="flex items-center justify-center">
          {React.cloneElement(icon, { className: 'w-6 h-6 text-[#393939] shrink-0 object-contain' })}
        </div>
      )}
      <span style={fieldStyles.title}>
        {intl.formatMessage({ id: titleKey, defaultMessage: titleKey })}
        {required && '*'}
      </span>
    </div>
  );

  if (title === 'tourAccessInfo') {
    return (
      <div className="mb-1 mt-1">
        {heading}
        <div style={fieldStyles.subtitle}>
          <FormattedMessage id="tourAccessInfoTip" />
        </div>
      </div>
    );
  }

  return heading;
};

const customTitleTemplate = () => null;

const mergeForPriceCalculation = (globalData = {}, localData = {}) => {
  const merged = deepMerge(globalData || {}, localData || {});
  return merged && typeof merged === 'object' ? merged : {};
};

const cleanPayload = (obj) => {
  if (obj === null || obj === undefined) return undefined;
  if (typeof obj !== 'object') {
    if (obj === '') return undefined;
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => cleanPayload(item)).filter(item => item !== undefined);
  }
  const cleanedObj = {};
  for (const key of Object.keys(obj)) {
    const value = cleanPayload(obj[key]);
    if (value !== undefined) {
      cleanedObj[key] = value;
    }
  }
  return cleanedObj;
};




const OrderForm = React.forwardRef(function OrderForm(props, ref) {
  const {
    formData: incomingFormData,
    handleError,
    onChange,
    onSubmit,
    orderType,
    previousOrderFile,
    schema: incomingSchema,
    uiSchema: incomingUiSchema,
    formContext: incomingFormContext,
    submitted,
  } = props;

  const intl = useIntl();
  const me = useContext(UserContext);
  const schema = useMemo(
    () => (me?.profile?.country === 'CO' ? makeZipcodeOptional(incomingSchema) : incomingSchema),
    [incomingSchema, me?.profile?.country]
  );

  const submitBtnRef = useRef(null);
  const priceTimerRef = useRef(null);
  const schemaKeyRef = useRef('');

  const [internalFormData, setInternalFormData] = useState(incomingFormData || {});
  const [internalUiSchema, setInternalUiSchema] = useState(incomingUiSchema || {});
  const [minimalPrice, setMinimalPrice] = useState(null);

  const incomingFormDataRef = useRef(incomingFormData);
  const onChangeRef = useRef(onChange);
  const validateRef = useRef(null);

  useEffect(() => {
    incomingFormDataRef.current = incomingFormData;
    onChangeRef.current = onChange;
  });

  useImperativeHandle(ref, () => ({
    get submitButton() {
      return submitBtnRef.current;
    },
  }));

  const fields = useMemo(() => ({
    addressSelectorField: AddressField,
    person: Person,
    people: People,
    customDisableField: CustomFieldDisable,
    customPreviousFile: RequestFormPreviousFile,
    customPreviousPanel: RequestFormPreviousPanel,
    customEditDisableField: EditRequestFormFieldDisable,
    customFileUpload: FileUpload,
    autoCompleteOrCreate: AutoCompleteOrCreate,
    autoCompleteOrCreateAddress: AutoCompleteOrCreate,
    attachments: AttachmentField,
    docusignFile: DocuSignFile,
    attachmentFiles: AttachmentFiles,
    docuSignSignersField: DocuSignSignersField,
    brySignersField: BrySignersField,
    keysLocationField: KeysLocationField,
    realEstateAdressesSelectField: RealEstateAdressesSelectField,
    recipientsField: RecipientsField,
    requestInfoField: RequestInfoField,
    addressField: AddressField,
    accessInformationField: AccessInformationField,
    propertyAccessField: PropertyAccessField,
    tagsField: TagsFieldTemplate,
  }), []);

  const widgets = useMemo(
    () => makeWidgetsFn({ country: me?.profile?.country }),
    [me?.profile?.country],
  );

  const consolidatedPriceData = useMemo(
    () => mergeForPriceCalculation(incomingFormContext?.allFormData || {}, internalFormData || {}),
    [incomingFormContext?.allFormData, internalFormData]
  );


  const calculateOrderPrice = useCallback(async (localStepFormData) => {
    const consolidatedFormData = mergeForPriceCalculation(
      incomingFormContext?.allFormData || {},
      localStepFormData || {},
    );

    const typeToCheck = consolidatedFormData?.type || consolidatedFormData?.inspection_type || props.orderType;
    const isExcludedType = typeToCheck?.startsWith('marketplace_') || ['docusign', 'fichacerta', 'bry', 'visitaDigital'].includes(typeToCheck);
    if (isExcludedType || !consolidatedFormData || Object.keys(consolidatedFormData).length === 0) return;

    const currentInspectionType = consolidatedFormData?.inspection_type || props.orderType;
    const isPhotoCapture = currentInspectionType === 'fotosDeCaptacao';
    const isTourVirtual = currentInspectionType === 'tourVirtual';
    const isWorkFollowUp = currentInspectionType === 'acompanhamentoObraMM' || consolidatedFormData?.type === 'acompanhamentoObraMM';
    const hasClient = !!(
      consolidatedFormData?.clients?.client ||
      incomingFormContext?.clientId ||
      incomingFormContext?.me?.identifier
    );
    const buildingType = consolidatedFormData?.details?.building_type || consolidatedFormData?.photoBuildingDetails?.building_type;
    const furnished = consolidatedFormData?.details?.furnished || consolidatedFormData?.photoBuildingDetails?.furnished;
    const area = consolidatedFormData?.details?.area || consolidatedFormData?.photoBuildingDetails?.area;
    const modality = consolidatedFormData?.additionals?.modality;
    const urgency = consolidatedFormData?.additionals?.urgency;

    if (
      internalUiSchema?.details?.price?.['ui:options']?.forceDisabled === true ||
      !currentInspectionType ||
      !hasClient
    ) {
      return;
    }

    const isClientRole = incomingFormContext?.isClient || me?.authRole === 'ROLE_CLIENT';

    const detailsProperties =
      incomingSchema?.definitions?.details?.properties ||
      incomingSchema?.properties?.details?.properties ||
      {};
    const requiresArea = !!detailsProperties.area;
    const requiresFurnished = !!detailsProperties.furnished;

    if (!buildingType) return;
    if (requiresArea && !area) return;
    if (requiresFurnished && !furnished) return;

    if (!isClientRole && !isTourVirtual && !isWorkFollowUp) {
      const hasModality = !!incomingSchema?.properties?.additionals?.properties?.modality;
      const hasUrgency = !!incomingSchema?.properties?.additionals?.properties?.urgency;
      if ((hasModality && !modality) || (hasUrgency && !urgency)) {
        return;
      }
    }

    const priceParams = {
      clientId: incomingFormContext?.clientId || consolidatedFormData?.clients?.client || incomingFormContext?.me?.identifier,
      inspection_type: currentInspectionType,
      area,
      buildingType,
      furnished,
      modality,
      urgency,
      isWorkFollowUp,
      isPhotoCapture,
      isTourVirtual,
      includeBudget: consolidatedFormData?.budgetDuringInspection?.includeBudget,
    };
    const priceParamsStr = JSON.stringify(priceParams);
    const refToUse = incomingFormContext?.lastPriceParamsRef || priceTimerRef;

    if (refToUse.current === priceParamsStr) {
      return;
    }

    try {
      const currentPrice = consolidatedFormData?.details?.price;
      const safeFormData = JSON.parse(JSON.stringify(consolidatedFormData));
      if (safeFormData.details?.price !== undefined) delete safeFormData.details.price;

      if (safeFormData.type !== undefined) delete safeFormData.type;
      if (!safeFormData.inspection_type) safeFormData.inspection_type = currentInspectionType;

      const { data } = await API.post('/financial/orderprice', safeFormData);

      refToUse.current = priceParamsStr;

      const canPatchLocalStepPrice = !!incomingSchema?.properties?.details || !!internalFormData?.details;

      const patchLocalStepPrice = (updater) => {
        if (!canPatchLocalStepPrice) return;
        setInternalFormData((prev) => {
          const prevDetails = prev?.details || {};
          const details = { ...prevDetails };
          updater(details);
          
          if (details.price === prevDetails.price) {
            return prev;
          }
          
          return { ...prev, details };
        });
      };

      switch (data?.description) {
        case 'STANDARD_METER':
          patchLocalStepPrice((details) => {
            details.price = data.value;
          });
          if (currentPrice !== data.value) {
            onChange?.({ formData: { details: { price: data.value } } }, 'details.price');
          }
          setInternalUiSchema((s) => {
            const previous = s || {};
            const previousDetails = previous.details || {};
            const previousPrice = previousDetails.price || {};
            if (previousPrice['ui:widget'] === 'customPriceWidget' && previousPrice['ui:disabled'] === true) return previous;
            return {
              ...previous,
              details: {
                ...previousDetails,
                price: {
                  ...previousPrice,
                  'ui:widget': 'customPriceWidget',
                  'ui:disabled': true,
                  'ui:options': {
                    ...previousPrice['ui:options'],
                    unit: data.unit || 'credits'
                  }
                },
              },
            };
          });
          setMinimalPrice(null);
          break;
        case 'MONETARY':
          patchLocalStepPrice((details) => {
            details.price = currentPrice;
          });
          setInternalUiSchema((s) => {
            const previous = s || {};
            const previousDetails = previous.details || {};
            const previousPrice = previousDetails.price || {};
            if (previousPrice['ui:disabled'] === false) return previous;
            return {
              ...previous,
              details: {
                ...previousDetails,
                price: {
                  ...previousPrice,
                  'ui:disabled': false,
                },
              },
            };
          });
          setMinimalPrice(data.minimalPrice);
          break;
        default:
          if (!isClientRole) {
            setInternalUiSchema((s) => {
              const previous = s || {};
              const previousDetails = previous.details || {};
              const previousPrice = previousDetails.price || {};
              if (previousPrice['ui:widget'] === 'customPriceWidget' && previousPrice['ui:disabled'] === false) return previous;
              return {
                ...previous,
                details: {
                  ...previousDetails,
                  price: {
                    ...previousPrice,
                    'ui:widget': 'customPriceWidget',
                    'ui:disabled': false,
                  },
                },
              };
            });
            setMinimalPrice(null);
          } else {
            patchLocalStepPrice((details) => {
              delete details.price;
            });
            if (currentPrice !== undefined) {
              onChange?.({ formData: { details: { price: undefined } } }, 'details.price');
            }
            setInternalUiSchema((s) => {
              const previous = s || {};
              const previousDetails = previous.details || {};
              const previousPrice = previousDetails.price || {};
              if (previousPrice['ui:widget'] === 'customPriceStringWidget') return previous;
              return {
                ...previous,
                details: {
                  ...previousDetails,
                  price: {
                    'ui:widget': 'customPriceStringWidget',
                    'ui:options': { label: false },
                  },
                },
              };
            });
            setMinimalPrice(null);
          }
          break;
      }
    } catch (err) {
      console.error('[DEBUG] POST /financial/orderprice API call error', err);
      if (!isClientRole) {
        setInternalUiSchema((s) => {
          const previous = s || {};
          const previousDetails = previous.details || {};
          const previousPrice = previousDetails.price || {};
          if (previousPrice['ui:widget'] === 'customPriceWidget' && previousPrice['ui:disabled'] === false) return previous;
          return {
            ...previous,
            details: {
              ...previousDetails,
              price: {
                ...previousPrice,
                'ui:widget': 'customPriceWidget',
                'ui:disabled': false,
              },
            },
          };
        });
      } else {
        setInternalUiSchema((s) => {
          const previous = s || {};
          const previousDetails = previous.details || {};
          const previousPrice = previousDetails.price || {};
          if (previousPrice['ui:widget'] === 'customPriceStringWidget') return previous;
          return {
            ...previous,
            details: {
              ...previousDetails,
              price: {
                'ui:widget': 'customPriceStringWidget',
                'ui:options': { label: false },
              },
            },
          };
        });
      }
      setMinimalPrice(null);
    }
  }, [
    props.orderType,
    internalUiSchema?.details?.price,
    incomingFormContext?.allFormData,
    incomingFormContext?.clientId,
    incomingFormContext?.me?.identifier,
    incomingSchema,
    internalFormData?.details,
    onChange,
  ]);

  useEffect(() => {
    if (!internalFormData || Object.keys(internalFormData).length === 0) return;

    clearTimeout(priceTimerRef.current);
    priceTimerRef.current = setTimeout(() => {
      calculateOrderPrice(internalFormData);
    }, 400);

    return () => {
      clearTimeout(priceTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    consolidatedPriceData?.clients?.client,
    consolidatedPriceData?.inspection_type,
    consolidatedPriceData?.type,
    consolidatedPriceData?.details?.area,
    consolidatedPriceData?.details?.building_type,
    consolidatedPriceData?.details?.furnished,
    consolidatedPriceData?.photoBuildingDetails?.area,
    consolidatedPriceData?.photoBuildingDetails?.building_type,
    consolidatedPriceData?.photoBuildingDetails?.furnished,
    consolidatedPriceData?.additionals?.modality,
    consolidatedPriceData?.additionals?.urgency,
    consolidatedPriceData?.budgetDuringInspection?.includeBudget,
  ]);




  useEffect(() => {
    const schemaKey = JSON.stringify(incomingSchema || {});
    const schemaChanged = schemaKeyRef.current !== schemaKey;
    
    const hasInternal = !!internalFormData && Object.keys(internalFormData).length > 0;
    const hasIncoming = !!incomingFormData && Object.keys(incomingFormData).length > 0;

    if (schemaChanged || (!hasInternal && hasIncoming)) {
      schemaKeyRef.current = schemaKey;
      setInternalFormData(incomingFormData || {});
      if (!hasInternal && hasIncoming) calculateOrderPrice(incomingFormData);
    }
  }, [incomingSchema, incomingFormData, calculateOrderPrice]);

  useEffect(() => {
    setInternalUiSchema(incomingUiSchema || {});
  }, [incomingUiSchema]);

  const handleChange = useCallback(({ formData: newFormData }, id) => {
    setInternalFormData(newFormData);

    if (JSON.stringify(newFormData) !== JSON.stringify(incomingFormDataRef.current || {})) {
      onChangeRef.current?.({ formData: newFormData }, id);
    }

    handleError(0);
  }, [handleError]);

  const handleSubmit = useCallback(({ formData }) => {
    onChangeRef.current?.({ formData });
    onSubmit({ formData });
  }, [onSubmit]);

  const transformErrors = useCallback((errors) =>
    errors
      .filter((err) => err.name !== 'enum' && err.name !== 'oneOf')
      .map((err) => {
        const msgId = transformErrorsMessages[err.name];
        if (!msgId) return err;
        if (err.name === 'format' && err.params?.format === 'date-with-time') {
          err.message = intl.formatMessage({ id: 'invalidDateTimeFormat' });
        } else if (err.name === 'format' && err.params?.format === 'date') {
          err.message = intl.formatMessage({ id: 'invalidFormat' });
        } else if (err.name === 'type' && err.params?.type === 'array') {
          err.message = intl.formatMessage({ id: 'shouldBeAnArray' });
        } else {
          err.message = intl.formatMessage({ id: msgId });
        }
        return err;
      }),
  [intl]);

  const onFormError = useCallback((errors) => {
    const transformed = transformErrors(errors);
    console.error('[OrderForm] Form validation errors:', transformed, errors);
    if (transformed?.length > 0) {
      handleError(transformed.length);
    } else {
      handleError(0);
    }
  }, [handleError, transformErrors]);

  const validate = useCallback((formData, errors) => {
    if (minimalPrice !== null) {
      if (formData.details?.price < minimalPrice || formData.details?.price === undefined) {
        errors.details?.price?.addError?.(
          intl.formatMessage({ id: 'minimumPrice' }, { meters: minimalPrice })
        );
      }
    }

    if (formData?.recipients?.length) {
      formData.recipients.forEach((data, index) => {
        if (data.email && !data.email.includes('@')) {
          errors.recipients?.[index]?.email?.addError?.(
            intl.formatMessage({ id: 'requiredEmail' })
          );
        }
      });
    }

    const isClient = me?.authRole === 'ROLE_CLIENT';
    const isNewOrder = incomingFormContext?.isNewOrder ?? props.isNewOrder;

    if (!isClient && isNewOrder && !formData?.clients?.client) {
      errors.clients?.client?.addError?.(intl.formatMessage({ id: 'requiredField' }));
    }

    return errors;
  }, [minimalPrice, intl, me, props.isNewOrder, incomingFormContext?.isNewOrder]);

  validateRef.current = validate;

  const stableValidate = useCallback((formData, errors) => {
    return validateRef.current?.(formData, errors) || errors;
  }, []);

  const templates = useMemo(() => ({
    ArrayFieldTemplate: orderType === 'marketplace_fichacerta' ? SuitorsArrayFieldTemplate : ArrayFieldTemplate,
    ObjectFieldTemplate: ObjectFieldTemplate,
    FieldTemplate: FieldTemplate,
    TitleFieldTemplate: customTitleTemplate,
  }), [orderType]);

  const stableClientId = useMemo(() => {
    return internalFormData?.clients?.client || incomingFormContext?.clientId || null;
  }, [internalFormData?.clients?.client, incomingFormContext?.clientId]);

  const stableFormContext = useMemo(() => ({
    isNewOrder: props.isNewOrder,
    orderType,
    me,
    ...incomingFormContext,
    clientId: stableClientId
  }), [props.isNewOrder, orderType, me, incomingFormContext, stableClientId]);

  if (!hasFormData(incomingSchema)) return <Loading borderColor="#fff" paperBackground="#fff" />;

  return (
    <Form
      schema={schema}
      uiSchema={internalUiSchema}
      formData={internalFormData}
      onSubmit={handleSubmit}
      onChange={handleChange}
      formContext={stableFormContext}
      fields={fields}
      widgets={widgets}
      templates={templates}
      showErrorList={false}
      omitExtraData={false}
      liveValidate={submitted}
      transformErrors={transformErrors}
      onError={onFormError}
      customValidate={stableValidate}
      validator={validator}
    >
      <button type="submit" data-cy="order-form-submit" ref={submitBtnRef} hidden />
    </Form>
  );
});

OrderForm.displayName = 'OrderForm';

OrderForm.propTypes = {
  formData: PropTypes.object,
  handleError: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  orderType: PropTypes.string.isRequired,
  previousOrderFile: PropTypes.string,
  schema: PropTypes.object,
  submitted: PropTypes.bool,
  uiSchema: PropTypes.object,
};

OrderForm.defaultProps = {
  formData: {},
  onChange: null,
  previousOrderFile: null,
  schema: {},
  submitted: false,
  uiSchema: {},
};

export default OrderForm;
